import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppShell } from '../components/AppShell'
import { Dialog } from '../components/Dialog'
import { Field } from '../components/Field'
import { useEntityList, useEntitySave, useEntityDelete, makeEntity, now } from '../api/hooks/useEntity'
import type { SchoolClass } from '../types/entities'
import { ENTITY } from '../types/entities'

/** Список классов учителя. CRUD + архивация. */
export function ClassesPage() {
  const { t } = useTranslation()
  const list = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const [editing, setEditing] = useState<SchoolClass | null>(null)
  const [showArchived, setShowArchived] = useState(false)

  const classes = (list.data ?? [])
    .filter((c) => (showArchived ? c.archivedAt != null : c.archivedAt == null))
    .sort((a, b) => a.gradeLevel - b.gradeLevel || a.name.localeCompare(b.name))

  return (
    <AppShell title={t('classesPage.title')} subtitle={t('classesPage.subtitle')}>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button
          onClick={() => setEditing(blankClass())}
          className="btn-primary text-sm"
        >
          {t('classesPage.addClass')}
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="w-4 h-4 accent-primary"
          />
          {t('classesPage.showArchive')}
        </label>
        <div className="text-sm text-gray-500 ml-auto">
          {list.isLoading ? t('common.loading') : t('classesPage.total', { count: classes.length })}
        </div>
      </div>

      {list.isError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm mb-4">
          {t('classesPage.loadError')}: {(list.error as Error).message}
        </div>
      )}

      {classes.length === 0 && !list.isLoading ? (
        <EmptyState onAdd={() => setEditing(blankClass())} archived={showArchived} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((c) => (
            <ClassCard key={c.id} cls={c} onEdit={() => setEditing(c)} />
          ))}
        </div>
      )}

      {editing && (
        <ClassFormDialog
          initial={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </AppShell>
  )
}

// ── Class card ────────────────────────────────────────────────────────────
function ClassCard({ cls, onEdit }: { cls: SchoolClass; onEdit: () => void }) {
  const { t } = useTranslation()
  const isArchived = cls.archivedAt != null
  return (
    <div className={`group relative bg-white border rounded-xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(87,10,166,0.2)] ${isArchived ? 'border-gray-200 opacity-60' : 'border-purple-100/80'}`}>
      <Link to={`/app/classes/${cls.id}`} className="block">
        <div className="flex items-start justify-between mb-3">
          <div className="text-2xl font-bold text-primary group-hover:text-primary-dark">
            {cls.name}
          </div>
          {cls.isClassTeacher && (
            <span className="text-[10px] bg-primary-soft text-primary px-2 py-0.5 rounded font-semibold">
              {t('classesPage.classTeacherBadge')}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-600">
          {t('classesPage.cardGradeYear', { grade: cls.gradeLevel, yearStart: cls.yearStart, yearEnd: cls.yearStart + 1 })}
        </div>
        {cls.description && (
          <div className="text-xs text-gray-500 mt-2 line-clamp-2">{cls.description}</div>
        )}
      </Link>
      <button
        onClick={onEdit}
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-gray-500 hover:text-primary px-2 py-1 rounded"
      >
        {t('classesPage.editButton')}
      </button>
    </div>
  )
}

// ── Form dialog ───────────────────────────────────────────────────────────
function ClassFormDialog({ initial, onClose }: { initial: SchoolClass; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useEntitySave<SchoolClass>(ENTITY.schoolClass)
  const del = useEntityDelete<SchoolClass>(ENTITY.schoolClass)
  const [name, setName] = useState(initial.name)
  const [gradeLevel, setGradeLevel] = useState(initial.gradeLevel)
  const [yearStart, setYearStart] = useState(initial.yearStart)
  const [description, setDescription] = useState(initial.description ?? '')
  const [isClassTeacher, setIsClassTeacher] = useState(initial.isClassTeacher ?? false)
  const [archived, setArchived] = useState(initial.archivedAt != null)

  const isNew = !initial.id

  const handleSave = async () => {
    if (!name.trim()) return
    const record = makeEntity<SchoolClass>({
      ...initial,
      name: name.trim(),
      gradeLevel,
      yearStart,
      description: description.trim() || null,
      isClassTeacher,
      archivedAt: archived ? (initial.archivedAt ?? now()) : null,
      updatedAt: now(),
    })
    await save.mutateAsync(record)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirm(t('classesPage.deleteConfirm', { name }))) return
    await del.mutateAsync(initial)
    onClose()
  }

  return (
    <Dialog title={isNew ? t('classesPage.dialogNewTitle') : t('classesPage.dialogEditTitle')} onClose={onClose}>
      <Field label={t('classesPage.fieldName')}>
        <input
          className="input-field"
          placeholder={t('classesPage.fieldNamePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('classesPage.fieldGradeLevel')}>
          <input
            type="number"
            min={1}
            max={11}
            className="input-field"
            value={gradeLevel}
            onChange={(e) => setGradeLevel(parseInt(e.target.value) || 1)}
          />
        </Field>
        <Field label={t('classesPage.fieldYearStart')}>
          <input
            type="number"
            min={2020}
            max={2040}
            className="input-field"
            value={yearStart}
            onChange={(e) => setYearStart(parseInt(e.target.value) || new Date().getFullYear())}
          />
        </Field>
      </div>
      <Field label={t('classesPage.fieldDescription')}>
        <textarea
          className="input-field min-h-[60px]"
          placeholder={t('classesPage.fieldDescriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div className="flex flex-col gap-2 mb-4">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 accent-primary"
            checked={isClassTeacher}
            onChange={(e) => setIsClassTeacher(e.target.checked)}
          />
          {t('classesPage.isClassTeacherLabel')}
        </label>
        {!isNew && (
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-primary"
              checked={archived}
              onChange={(e) => setArchived(e.target.checked)}
            />
            {t('classesPage.archivedLabel')}
          </label>
        )}
      </div>

      <div className="flex gap-2 justify-between flex-wrap">
        <div>
          {!isNew && (
            <button
              onClick={handleDelete}
              disabled={del.isPending}
              className="text-sm text-red-600 hover:text-red-700 px-3 py-2"
            >
              {t('common.delete')}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-outline text-sm">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={save.isPending || !name.trim()}
            className="btn-primary text-sm"
          >
            {save.isPending ? t('classesPage.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

function EmptyState({ onAdd, archived }: { onAdd: () => void; archived: boolean }) {
  const { t } = useTranslation()
  if (archived) {
    return (
      <div className="text-center py-12 text-gray-500">
        {t('classesPage.emptyArchived')}
      </div>
    )
  }
  return (
    <div className="text-center py-12 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
      <div className="text-5xl mb-4">📘</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('classesPage.emptyTitle')}</h3>
      <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto px-4">
        {t('classesPage.emptyText')}
      </p>
      <button onClick={onAdd} className="btn-primary text-sm">
        {t('classesPage.addFirstClass')}
      </button>
    </div>
  )
}


function blankClass(): SchoolClass {
  const year = new Date().getFullYear()
  return {
    id: '',
    name: '',
    gradeLevel: 5,
    yearStart: year,
    description: null,
    isClassTeacher: false,
    archivedAt: null,
    createdAt: 0,
    updatedAt: 0,
  }
}
